import { useRouter } from "next/router";

import { useSelector } from "react-redux";
import { FC, useEffect, useState } from "react";

import { FormikProvider, useFormik } from "formik";
import { toFormikValidationSchema } from "zod-formik-adapter";

import Box from "@mui/material/Box";

import { pinCodeSchema } from "validators/client/pinCode";

import { selectAvailableAddress } from "redux/slices/metamask";

import MintNFTStep from "sections/itemsPage/MintNFTStep";

import CustomOutlinedInput from "components/CustomOutlinedInput";
import Modal from "components/Modal";
import InfoBox from "components/InfoBox";
import { INFO_BOX_TYPE } from "components/InfoBox/types";

import { STORAGE } from "enums/storage";
import { NFC_MINT_TYPES } from "enums/nfcMintTypes";

import { API_ROUTES, APP_ROUTES } from "config/routes";

import { getMetamaskMessageError } from "utils/blockchain";
import { captureException } from "utils/errors";
import { setObjectItem } from "utils/storage";

import fetcher from "lib/fetchJson";

const initialState = { code: "" };

interface FormValues {
  code: string;
}

interface MintItemModalProps {
  data: {
    hash: string;
    contractAddress: string;
    nfcContractAddress?: string | null;
    tokenId: number;
  };
  additionalData: {
    nickname: string;
    email?: string;
    walletAddress?: string;
  };
  mintType: NFC_MINT_TYPES;
  isOpen?: boolean;
  isPinCodeRequired?: boolean;
  onClose?: () => void;
  isCards: boolean;
  onAfterMint?: () => void;
}

const MintItemModal: FC<MintItemModalProps> = ({
  data,
  isOpen,
  onClose,
  additionalData,
  isCards,
  isPinCodeRequired = false,
  onAfterMint,
  mintType,
}) => {
  const router = useRouter();
  const [isLoading, setIsLoading] = useState(false);
  const walletAddress = useSelector(selectAvailableAddress);

  const [errorMsg, setErrorMsg] = useState("");

  const handleMintAndRedeem = async (code: string) => {
    setObjectItem(STORAGE.FORM_DATA, additionalData);
    const { data: response } = await fetcher(
      `${
        isCards
          ? API_ROUTES.CARDS_MINT
          : mintType === NFC_MINT_TYPES.OFFCHAIN
          ? API_ROUTES.NFC_MINT_OFFCHAIN
          : API_ROUTES.NFC_MINT_ONCHAIN
      }/${data.hash}`,
      {
        ...(additionalData ?? {}),
        code,
        taskId: 1234,
      },
      "POST",
    );

    return response;
  };

  const handleSubmit = async (values: FormValues) => {
    setErrorMsg("");
    setIsLoading(true);
    const handleResponse = async () => {
      if (isCards) {
        await router.push(
          `${APP_ROUTES.ITEMS_NFT}/${data.contractAddress?.toLowerCase()}/${
            data.tokenId
          }`,
        );
      } else {
        onAfterMint && onAfterMint();
      }
    };

    try {
      const response = await handleMintAndRedeem(values.code);

      if (response.walletAddress || walletAddress) {
        await handleResponse();
      }
    } catch (error) {
      setIsLoading(false);
      captureException(error);
      setErrorMsg(
        getMetamaskMessageError(error.data ?? error, "Something went wrong"),
      );
    }
  };

  useEffect(() => {
    return () => {
      onClose && onClose();
    };
  }, [onClose]);

  const formik = useFormik({
    initialValues: initialState,
    enableReinitialize: true,
    validationSchema: isPinCodeRequired
      ? toFormikValidationSchema(pinCodeSchema)
      : null,
    onSubmit: handleSubmit,
  });

  return (
    <Modal
      isOpen={isOpen}
      handleClose={onClose}
      title="MINT YOUR NFT"
      disableBackdropClick
    >
      <Box maxWidth={676}>
        <FormikProvider value={formik}>
          <form>
            <MintNFTStep
              isLoading={isLoading}
              onPay={formik.submitForm}
              onCancel={onClose}
            >
              {isPinCodeRequired ? (
                <Box display="flex" width="100%">
                  <CustomOutlinedInput
                    inputProps={{
                      id: "code",
                      value: formik.values.code,
                      onChange: formik.handleChange,
                      placeholder: "Pin code*",
                    }}
                    error={formik.errors.code}
                  />
                </Box>
              ) : null}
            </MintNFTStep>
          </form>
        </FormikProvider>

        {errorMsg && (
          <Box width="100%" mt={2}>
            <InfoBox type={INFO_BOX_TYPE.REJECTED}>{errorMsg}</InfoBox>
          </Box>
        )}
      </Box>
    </Modal>
  );
};

export default MintItemModal;
